import React from "react";
import { Trash2, Save, Upload } from "lucide-react";

/**
 * ColourwayRow — single table row for the Colourways · Stock manager.
 * Handles inline-edit, save, delete, and per-colourway image upload.
 */
const ColourwayRow = ({
  cw,
  idx,
  preview,
  uploading,
  saving,
  onFieldChange,
  onSave,
  onDelete,
  onPickFile,
  onUpload,
  registerFileInput,
}) => (
  <tr className="border-b border-line" data-testid={`cw-row-${cw.slug}`}>
    <td className="px-3 py-2">
      <div className="flex items-start gap-2">
        <div className="relative w-12 h-12 border border-line overflow-hidden shrink-0">
          {preview ? (
            <img src={preview} alt={cw.name} className="w-full h-full object-cover" />
          ) : (
            <span className="block w-full h-full" style={{ backgroundColor: cw.hex }} />
          )}
        </div>
        <div>
          <input
            ref={(el) => registerFileInput(cw.slug, el)}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => onUpload(cw, e)}
            hidden
          />
          <button
            type="button"
            onClick={() => onPickFile(cw)}
            disabled={uploading}
            className="text-[10px] tracking-widest uppercase font-bold underline disabled:opacity-50 inline-flex items-center gap-1"
            data-testid={`cw-upload-${cw.slug}`}
          >
            <Upload size={10} />
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </td>
    <td className="px-3 py-2">
      <input
        value={cw.name}
        onChange={(e) => onFieldChange(idx, "name", e.target.value)}
        className="w-full border border-transparent hover:border-line focus:border-ink bg-transparent px-1.5 py-1"
      />
      <p className="mono text-[10px] text-mute mt-0.5">{cw.slug}</p>
    </td>
    <td className="px-3 py-2">
      <input
        value={cw.hex}
        onChange={(e) => onFieldChange(idx, "hex", e.target.value)}
        className="w-20 border border-line bg-chalk px-1.5 py-1 mono text-[10px]"
      />
    </td>
    <td className="px-3 py-2">
      <input
        value={cw.season || ""}
        onChange={(e) => onFieldChange(idx, "season", e.target.value)}
        className="w-16 border border-line bg-chalk px-1.5 py-1 mono text-[10px]"
      />
    </td>
    <td className="px-3 py-2">
      <input
        type="number"
        min="0"
        value={cw.stock ?? 0}
        onChange={(e) => onFieldChange(idx, "stock", e.target.value)}
        className={`w-16 border bg-chalk px-1.5 py-1 mono ${
          Number(cw.stock) <= 0
            ? "border-ink text-ink font-bold"
            : Number(cw.stock) <= 5
            ? "border-ink"
            : "border-line"
        }`}
        data-testid={`cw-stock-${cw.slug}`}
      />
    </td>
    <td className="px-3 py-2 text-center">
      <input
        type="checkbox"
        checked={Boolean(cw.featured_this_season)}
        onChange={(e) => onFieldChange(idx, "featured_this_season", e.target.checked)}
      />
    </td>
    <td className="px-3 py-2 text-center">
      <input
        type="checkbox"
        checked={Boolean(cw.is_active)}
        onChange={(e) => onFieldChange(idx, "is_active", e.target.checked)}
      />
    </td>
    <td className="px-3 py-2">
      <input
        type="number"
        value={cw.sort_order ?? 0}
        onChange={(e) => onFieldChange(idx, "sort_order", e.target.value)}
        className="w-12 border border-line bg-chalk px-1.5 py-1 mono"
      />
    </td>
    <td className="px-3 py-2 text-right whitespace-nowrap">
      {cw._dirty && (
        <button
          type="button"
          onClick={() => onSave(cw)}
          disabled={saving}
          className="text-[10px] tracking-widest uppercase font-bold px-2 py-1 bg-ink text-white mr-2"
          data-testid={`cw-save-${cw.slug}`}
        >
          <Save size={11} className="inline mr-1" />
          {saving ? "…" : "Save"}
        </button>
      )}
      <button
        type="button"
        onClick={() => onDelete(cw)}
        className="text-mute hover:text-ink p-1"
      >
        <Trash2 size={12} />
      </button>
    </td>
  </tr>
);

export default ColourwayRow;
